<template>
  <div v-if="isOpen" class="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
    <div class="w-full max-w-lg bg-gray-900 text-white rounded-lg p-6 flex flex-col gap-6">
      <div class="flex justify-between items-center">
        <h3 class="text-xl font-bold text-indigo-300">ยืนยันการสัก</h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-white" :disabled="isTattooing">
          <i class="fas fa-times text-xl"></i>
        </button>
      </div>

      <div v-if="tattoo" class="text-center">
        <img :src="tattoo.imageUrl" alt="Tattoo" class="w-32 h-32 rounded-md object-cover mx-auto mb-4" />
        <p class="text-sm font-semibold">{{ tattoo.name }}</p>
        <p class="text-xs text-indigo-300">{{ getEffectText(tattoo.stats) }}</p>
        <p v-if="selectedCurrency" class="text-xl font-bold text-gray-100 mt-2">
          {{ formatNumber(tattoo.cost[selectedCurrency]) }}
          <i :class="currencyIcons[selectedCurrency]" class="ml-1"></i>
        </p>
      </div>

      <div v-if="!confirm" class="flex flex-col gap-4">
        <div class="text-sm text-gray-300 text-center">
          <p>เลือกวิธีการชำระเงิน</p>
          <div class="grid grid-cols-4 gap-3 mt-3">
            <button
              v-if="tattoo.cost.cash > 0"
              @click="selectedCurrency = 'cash'"
              class="flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
              :class="{ 'border-2 border-indigo-500': selectedCurrency === 'cash' }"
            >
              {{ formatNumber(tattoo.cost.cash) }}
              <i class="fas fa-money-bill text-green-500"></i>
            </button>
            <button
              v-if="tattoo.cost.diamond > 0"
              @click="selectedCurrency = 'diamond'"
              class="flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
              :class="{ 'border-2 border-indigo-500': selectedCurrency === 'diamond' }"
            >
              {{ formatNumber(tattoo.cost.diamond) }}
              <i class="fas fa-gem text-blue-500"></i>
            </button>
            <button
              v-if="tattoo.cost.gold > 0"
              @click="selectedCurrency = 'gold'"
              class="flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
              :class="{ 'border-2 border-indigo-500': selectedCurrency === 'gold' }"
            >
              {{ formatNumber(tattoo.cost.gold) }}
              <i class="fas fa-g text-yellow-500"></i>
            </button>
            <button
              v-if="tattoo.cost.silver > 0"
              @click="selectedCurrency = 'silver'"
              class="flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
              :class="{ 'border-2 border-indigo-500': selectedCurrency === 'silver' }"
            >
              {{ formatNumber(tattoo.cost.silver) }}
              <i class="fas fa-coins text-gray-300"></i>
            </button>
          </div>
        </div>
        <button
          @click="confirm = true"
          class="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
          :disabled="!selectedCurrency"
        >
          สักลายนี้
        </button>
      </div>
      <div v-else class="flex justify-center gap-4">
        <button
          @click="tattooSkin"
          class="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-6 rounded-lg flex items-center gap-2 transition-colors"
          :disabled="isTattooing"
        >
          <span v-if="!isTattooing"><i class="fas fa-check mr-2"></i> ยืนยัน</span>
          <div v-else class="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
        </button>
        <button
          @click="confirm = false"
          class="bg-gray-600 hover:bg-gray-700 text-white py-2 px-6 rounded-lg transition-colors"
          :disabled="isTattooing"
        >
          ยกเลิก
        </button>
      </div>

      <p v-if="error" class="text-red-500 text-sm text-center">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { projectFunctions } from '@/firebase/config';

export default {
  name: 'ConfirmTattooModal',
  props: {
    isOpen: Boolean,
    tattoo: Object
  },
  emits: ['close', 'tattoo-confirmed'],
  setup(props, { emit }) {
    const confirm = ref(false);
    const isTattooing = ref(false);
    const error = ref('');
    const selectedCurrency = ref(null); // Changed from 'silver' to null

    const currencyIcons = {
      cash: 'fas fa-money-bill text-green-500',
      diamond: 'fas fa-gem text-blue-500',
      gold: 'fas fa-g text-yellow-500',
      silver: 'fas fa-coins text-gray-300'
    };

    // ฟังก์ชันสำหรับเพิ่มคอมมาในตัวเลข
    const formatNumber = (number) => {
      if (typeof number !== 'number' || isNaN(number)) return '0';
      return number.toLocaleString('en-US');
    };

    const getEffectText = (stats) => {
      const effects = [];
      if (stats.str > 0) effects.push(`+${stats.str} STR`);
      if (stats.stamina > 0) effects.push(`+${stats.stamina} Stamina`);
      if (stats.int > 0) effects.push(`+${stats.int} INT`);
      if (stats.agi > 0) effects.push(`+${stats.agi} AGI`);
      if (stats.luck > 0) effects.push(`+${stats.luck} LUCK`);
      return effects.join(', ');
    };

    const tattooSkin = async () => {
      if (!props.tattoo || !selectedCurrency.value) return;

      isTattooing.value = true;
      error.value = '';
      try {
        const fn = projectFunctions.httpsCallable('gameActions');
        const result = await fn({
          action: 'characterActions',
          subAction: 'tattooSkin',
          tattooName: props.tattoo.name,
          currency: selectedCurrency.value
        });
        emit('tattoo-confirmed', props.tattoo.name, result.data.newBalances);
        confirm.value = false;
        selectedCurrency.value = null; // Reset to null after confirmation
      } catch (err) {
        console.error('Error tattooing:', err);
        error.value = err.message || 'เกิดข้อผิดพลาดในการสัก';
      } finally {
        isTattooing.value = false;
      }
    };

    return { confirm, isTattooing, error, selectedCurrency, currencyIcons, formatNumber, getEffectText, tattooSkin };
  }
};
</script>

<style scoped>
/* เพิ่มการปรับแต่งเพิ่มเติมสำหรับความสวยงาม */
button {
  transition: all 0.2s ease-in-out;
}

button:hover:not(:disabled) {
  transform: translateY(-1px);
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.grid button {
  min-height: 48px;
  font-size: 0.875rem;
  font-weight: 500;
}
</style>